<script setup lang="ts">
import { EmptyState, TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { computed, onMounted, reactive, ref } from 'vue'

import {
  STATUS_LABELS as CHARGE_STATUS,
  listCharges,
  type ChargeRow,
  type ChargeStatus,
} from '../api/charges'
import { customerOption, listCustomerOptions, type Customer } from '../api/customers'
import {
  SOURCE_LABELS,
  STATUS_LABELS,
  STATUS_TAG,
  canAct,
  cancelOrder,
  chargeOrder,
  confirmOrder,
  createOrder,
  deleteOrder,
  getOrder,
  listOrders,
  signOrder,
  transitOrder,
  updateOrder,
  type ListRow,
  type Order,
  type OrderAction,
  type OrderInput,
  type OrderItemInput,
  type OrderSource,
  type OrderStatus,
} from '../api/orders'
import { listActiveRateCards, type RateCard } from '../api/rates'

const rows = ref<ListRow[]>([])
const total = ref(0)
const loading = ref(false)
const error = ref<unknown>(null)
const notice = ref('')

const customers = ref<Customer[]>([])
const refError = ref<unknown>(null)

// 参照下拉用 undefined 表示「未选」：0 会被 el-select 当成值显示成「0」，placeholder 就没了
const query = reactive({
  keyword: '',
  customer: undefined as number | undefined,
  status: '',
  source: '',
  page: 1,
  size: 20,
})

const formOpen = ref(false)
const editing = ref<ListRow | null>(null)
const saving = ref(false)
const formError = ref<unknown>(null)
const form = reactive({
  customer_id: undefined as number | undefined,
  consignee: '',
  consignee_phone: '',
  origin: '',
  dest: '',
  expect_pickup_at: null as string | null,
  expect_deliver_at: null as string | null,
  distance_km: 0,
  weight_kg: 0,
  volume_cm3: 0,
  cargo_desc: '',
  remark: '',
  items: [] as OrderItemInput[],
})

const detailOpen = ref(false)
const detailLoading = ref(false)
const detailError = ref<unknown>(null)
const detail = ref<{ order: Order; items: OrderItemInput[]; customer_name: string } | null>(null)
const detailCharges = ref<ChargeRow[]>([])

const chargeOpen = ref(false)
const chargeRow = ref<ListRow | null>(null)
const chargeRateId = ref<number | undefined>(undefined)
const rateCards = ref<RateCard[]>([])
const charging = ref(false)
const chargeError = ref<unknown>(null)

const formErrorText = computed(() => {
  if (!form.customer_id) return '请选择客户'
  if (!form.origin.trim()) return '请填写起运地'
  if (!form.dest.trim()) return '请填写目的地'
  if (!form.items.length) return '请至少添加一行货品明细'
  for (const [i, item] of form.items.entries()) {
    if (!item.name.trim()) return `第 ${i + 1} 行请填写货品名称`
    if (!(Number(item.qty) > 0)) return `第 ${i + 1} 行数量必须大于 0`
  }
  return ''
})

async function load(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const res = await listOrders({
      page: query.page,
      size: query.size,
      keyword: query.keyword.trim() || undefined,
      customer: query.customer,
      status: query.status || undefined,
      source: query.source || undefined,
    })
    rows.value = res.list
    total.value = res.total
    notice.value = noticeText
  } catch (e) {
    error.value = e
    rows.value = []
  } finally {
    loading.value = false
  }
}

async function loadRefs(): Promise<void> {
  refError.value = null
  try {
    customers.value = await listCustomerOptions()
  } catch (e) {
    // 客户下拉拿不到，建单入口就用不了，必须把原因露出来（如没有 ops:customer 读权限）
    refError.value = e
  }
}

onMounted(() => {
  void load()
  void loadRefs()
})

function search(): void {
  query.page = 1
  void load()
}

function page(p: number): void {
  query.page = p
  void load()
}

/** 后端时间是 RFC3339（带时区偏移），取到秒并还原成本地墙钟时间给日期控件 */
function toLocal(iso: string | null): string | null {
  if (!iso) return null
  return `${iso.slice(0, 10)} ${iso.slice(11, 19)}`
}

function resetForm(): void {
  Object.assign(form, {
    customer_id: customers.value[0]?.id,
    consignee: '',
    consignee_phone: '',
    origin: '',
    dest: '',
    expect_pickup_at: null,
    expect_deliver_at: null,
    distance_km: 0,
    weight_kg: 0,
    volume_cm3: 0,
    cargo_desc: '',
    remark: '',
    items: [] as OrderItemInput[],
  })
}

function openCreate(): void {
  editing.value = null
  resetForm()
  formError.value = null
  formOpen.value = true
}

async function openEdit(row: ListRow): Promise<void> {
  editing.value = row
  resetForm()
  formError.value = null
  formOpen.value = true
  try {
    const d = await getOrder(row.id)
    form.customer_id = d.order.customer_id
    form.consignee = d.order.consignee
    form.consignee_phone = d.order.consignee_phone
    form.origin = d.order.origin
    form.dest = d.order.dest
    form.expect_pickup_at = toLocal(d.order.expect_pickup_at)
    form.expect_deliver_at = toLocal(d.order.expect_deliver_at)
    form.distance_km = d.order.distance_km
    form.weight_kg = d.order.weight_kg
    form.volume_cm3 = d.order.volume_cm3
    form.cargo_desc = d.order.cargo_desc
    form.remark = d.order.remark
    form.items = d.items.map((item) => ({
      name: item.name,
      qty: item.qty,
      unit: item.unit,
      weight_kg: item.weight_kg,
      volume_cm3: item.volume_cm3,
    }))
  } catch (e) {
    formError.value = e
  }
}

function addItem(): void {
  form.items.push({ name: '', qty: 1, unit: '', weight_kg: 0, volume_cm3: 0 })
}

function removeItem(index: number): void {
  form.items.splice(index, 1)
}

async function save(): Promise<void> {
  if (formErrorText.value) return
  saving.value = true
  formError.value = null
  const input: OrderInput = {
    customer_id: form.customer_id ?? 0,
    consignee: form.consignee.trim(),
    consignee_phone: form.consignee_phone.trim(),
    origin: form.origin.trim(),
    dest: form.dest.trim(),
    expect_pickup_at: form.expect_pickup_at,
    expect_deliver_at: form.expect_deliver_at,
    distance_km: Number(form.distance_km) || 0,
    weight_kg: Number(form.weight_kg) || 0,
    volume_cm3: Number(form.volume_cm3) || 0,
    cargo_desc: form.cargo_desc.trim(),
    remark: form.remark.trim(),
    items: form.items.map((item) => ({
      name: item.name.trim(),
      qty: Number(item.qty),
      unit: item.unit ?? '',
      weight_kg: Number(item.weight_kg) || 0,
      volume_cm3: Number(item.volume_cm3) || 0,
    })),
  }
  try {
    if (editing.value) await updateOrder(editing.value.id, input)
    else await createOrder(input)
    formOpen.value = false
    await load(editing.value ? '订单已更新' : '订单已创建')
  } catch (e) {
    formError.value = e
  } finally {
    saving.value = false
  }
}

/** 状态流转统一走这里：成功刷新并提示，失败把错误挂到告警区 */
async function act(row: ListRow, action: OrderAction, text: string): Promise<void> {
  notice.value = ''
  error.value = null
  try {
    if (action === 'confirm') await confirmOrder(row.id)
    else if (action === 'cancel') await cancelOrder(row.id)
    else if (action === 'transit') await transitOrder(row.id)
    else if (action === 'sign') await signOrder(row.id)
    else await chargeOrder(row.id)
    await load(text)
  } catch (e) {
    error.value = e
  }
}

async function remove(row: ListRow): Promise<void> {
  notice.value = ''
  error.value = null
  try {
    await deleteOrder(row.id)
    await load(`已删除订单「${row.doc_no}」`)
  } catch (e) {
    error.value = e
  }
}

async function openDetail(row: ListRow): Promise<void> {
  detailOpen.value = true
  detail.value = null
  detailCharges.value = []
  detailError.value = null
  detailLoading.value = true
  try {
    const [d, charges] = await Promise.all([
      getOrder(row.id),
      listCharges({ page: 1, size: 200, order_id: row.id }),
    ])
    detail.value = {
      order: d.order,
      customer_name: d.customer_name,
      items: d.items.map((item) => ({
        name: item.name,
        qty: item.qty,
        unit: item.unit,
        weight_kg: item.weight_kg,
        volume_cm3: item.volume_cm3,
      })),
    }
    detailCharges.value = charges.list
  } catch (e) {
    detailError.value = e
  } finally {
    detailLoading.value = false
  }
}

async function openCharge(row: ListRow): Promise<void> {
  chargeRow.value = row
  chargeRateId.value = undefined
  chargeError.value = null
  chargeOpen.value = true
  if (rateCards.value.length) return
  try {
    rateCards.value = await listActiveRateCards()
  } catch (e) {
    chargeError.value = e
  }
}

async function doCharge(): Promise<void> {
  if (!chargeRow.value) return
  charging.value = true
  chargeError.value = null
  try {
    await chargeOrder(chargeRow.value.id, chargeRateId.value)
    const docNo = chargeRow.value.doc_no
    chargeOpen.value = false
    await load(`订单「${docNo}」已生成计费明细`)
  } catch (e) {
    chargeError.value = e
  } finally {
    charging.value = false
  }
}
</script>

<template>
  <div class="yc-order">
    <header class="yc-order__bar">
      <el-input
        v-model="query.keyword"
        class="yc-order__keyword"
        placeholder="订单号 / 收货人"
        clearable
        @keyup.enter="search"
        @clear="search"
      />
      <el-select
        v-model="query.customer"
        class="yc-order__ref"
        placeholder="全部客户"
        clearable
        filterable
        @change="search"
      >
        <el-option v-for="c in customers" :key="c.id" :label="customerOption(c)" :value="c.id" />
      </el-select>
      <el-select v-model="query.status" class="yc-order__status" placeholder="全部状态" clearable @change="search">
        <el-option v-for="(label, value) in STATUS_LABELS" :key="value" :label="label" :value="value" />
      </el-select>
      <el-select v-model="query.source" class="yc-order__status" placeholder="全部来源" clearable @change="search">
        <el-option v-for="(label, value) in SOURCE_LABELS" :key="value" :label="label" :value="value" />
      </el-select>
      <el-button @click="search">查询</el-button>
      <el-button v-perm="'ops:order:create'" type="primary" @click="openCreate">新建订单</el-button>
      <el-button @click="load()">刷新</el-button>
      <span v-if="notice" class="yc-order__notice">{{ notice }}</span>
    </header>

    <div v-if="error || refError" class="yc-order__error">
      <TraceIdAlert :error="error ?? refError" />
    </div>

    <el-card v-loading="loading" class="yc-order__card">
      <el-table v-if="rows.length" :data="rows" row-key="id">
        <el-table-column prop="doc_no" label="订单号" width="170" />
        <el-table-column label="客户" min-width="140">
          <template #default="{ row }">{{ row.customer_name || `#${row.customer_id}` }}</template>
        </el-table-column>
        <el-table-column label="收货人" width="150">
          <template #default="{ row }">{{ row.consignee }} {{ row.consignee_phone }}</template>
        </el-table-column>
        <el-table-column label="线路" min-width="170" show-overflow-tooltip>
          <template #default="{ row }">{{ row.origin }} → {{ row.dest }}</template>
        </el-table-column>
        <el-table-column label="状态" width="96">
          <template #default="{ row }">
            <el-tag :type="STATUS_TAG[row.status as OrderStatus]" size="small">
              {{ STATUS_LABELS[row.status as OrderStatus] ?? row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="来源" width="90">
          <template #default="{ row }">{{ SOURCE_LABELS[row.source as OrderSource] ?? row.source }}</template>
        </el-table-column>
        <el-table-column prop="total_qty" label="件数" width="80" align="right" />
        <el-table-column prop="charge_count" label="计费" width="80" align="right" />
        <el-table-column label="创建时间" min-width="155">
          <template #default="{ row }">{{ formatDateTime(row.created_at) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="300" fixed="right">
          <template #default="{ row }">
            <el-button link type="primary" @click="openDetail(row)">详情</el-button>
            <el-button
              v-if="row.status === 'draft'"
              v-perm="'ops:order:update'"
              link
              type="primary"
              @click="openEdit(row)"
            >
              编辑
            </el-button>
            <el-button
              v-if="canAct(row.status, 'confirm')"
              v-perm="'ops:order:confirm'"
              link
              type="primary"
              @click="act(row, 'confirm', `订单「${row.doc_no}」已确认`)"
            >
              确认
            </el-button>
            <el-button
              v-if="canAct(row.status, 'transit')"
              v-perm="'ops:order:transit'"
              link
              type="warning"
              @click="act(row, 'transit', `订单「${row.doc_no}」已发运在途`)"
            >
              在途
            </el-button>
            <el-button
              v-if="canAct(row.status, 'sign')"
              v-perm="'ops:order:sign'"
              link
              type="success"
              @click="act(row, 'sign', `订单「${row.doc_no}」已签收`)"
            >
              签收
            </el-button>
            <!-- 后端对已有非作废计费的订单重复计费回 409，这里按列表的 charge_count 先藏掉按钮 -->
            <el-button
              v-if="canAct(row.status, 'charge') && row.charge_count === 0"
              v-perm="'ops:order:charge'"
              link
              type="primary"
              @click="openCharge(row)"
            >
              计费
            </el-button>
            <el-popconfirm
              v-if="canAct(row.status, 'cancel')"
              title="确认取消该订单？取消后不可恢复。"
              width="240"
              @confirm="act(row, 'cancel', `订单「${row.doc_no}」已取消`)"
            >
              <template #reference>
                <el-button v-perm="'ops:order:cancel'" link type="danger">取消</el-button>
              </template>
            </el-popconfirm>
            <el-popconfirm
              v-if="row.status === 'draft'"
              title="确认删除该订单？"
              width="200"
              @confirm="remove(row)"
            >
              <template #reference>
                <!-- 后端删除鉴权用的是 ops:order:update（yaml 里没有单独的删除码） -->
                <el-button v-perm="'ops:order:update'" link type="danger">删除</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>

      <EmptyState
        v-else-if="!loading"
        title="还没有运输订单"
        description="填客户与起止地就能建单，签收后可按规则生成应收。"
      >
        <el-button v-perm="'ops:order:create'" type="primary" @click="openCreate">新建订单</el-button>
      </EmptyState>
    </el-card>

    <el-pagination
      v-if="total > query.size"
      class="yc-order__page"
      layout="total, prev, pager, next"
      :total="total"
      :page-size="query.size"
      :current-page="query.page"
      @current-change="page"
    />

    <!-- 新建 / 编辑 -->
    <el-dialog
      v-if="formOpen"
      v-model="formOpen"
      :title="editing ? `编辑订单 · ${editing.doc_no}` : '新建订单'"
      width="760px"
      :close-on-click-modal="false"
    >
      <div v-if="formError" class="yc-order__error">
        <TraceIdAlert :error="formError" />
      </div>

      <el-form label-position="top" @submit.prevent="save">
        <div class="yc-order__formrow">
          <el-form-item label="客户">
            <el-select v-model="form.customer_id" class="yc-order__ref" filterable>
              <el-option v-for="c in customers" :key="c.id" :label="customerOption(c)" :value="c.id" />
            </el-select>
          </el-form-item>
          <el-form-item label="收货人">
            <el-input v-model="form.consignee" class="yc-order__small" maxlength="64" placeholder="如 王五" />
          </el-form-item>
          <el-form-item label="收货电话">
            <el-input v-model="form.consignee_phone" class="yc-order__small" maxlength="32" placeholder="如 13800000000" />
          </el-form-item>
        </div>

        <div class="yc-order__formrow">
          <el-form-item label="起运地">
            <el-input v-model="form.origin" class="yc-order__small" maxlength="128" placeholder="如 上海" />
          </el-form-item>
          <el-form-item label="目的地">
            <el-input v-model="form.dest" class="yc-order__small" maxlength="128" placeholder="如 杭州" />
          </el-form-item>
          <el-form-item label="货品描述">
            <el-input v-model="form.cargo_desc" class="yc-order__small" maxlength="255" placeholder="可留空" />
          </el-form-item>
        </div>

        <div class="yc-order__formrow">
          <el-form-item label="期望提货时间">
            <el-date-picker
              v-model="form.expect_pickup_at"
              type="datetime"
              value-format="YYYY-MM-DD HH:mm:ss"
              placeholder="可留空"
            />
          </el-form-item>
          <el-form-item label="期望送达时间">
            <el-date-picker
              v-model="form.expect_deliver_at"
              type="datetime"
              value-format="YYYY-MM-DD HH:mm:ss"
              placeholder="可留空"
            />
          </el-form-item>
        </div>

        <div class="yc-order__formrow">
          <el-form-item label="里程（km）">
            <el-input-number v-model="form.distance_km" :min="0" :max="999999" :precision="2" controls-position="right" />
          </el-form-item>
          <el-form-item label="重量（kg）">
            <el-input-number v-model="form.weight_kg" :min="0" :max="999999999" :precision="3" controls-position="right" />
          </el-form-item>
          <el-form-item label="体积（cm³）">
            <el-input-number v-model="form.volume_cm3" :min="0" :max="999999999" :precision="2" controls-position="right" />
          </el-form-item>
          <el-form-item label="备注">
            <el-input v-model="form.remark" class="yc-order__small" maxlength="255" placeholder="可留空" />
          </el-form-item>
        </div>

        <el-form-item label="货品明细">
          <div class="yc-order__lines">
            <div v-for="(item, index) in form.items" :key="index" class="yc-order__line">
              <el-input v-model="item.name" class="yc-order__name" maxlength="128" placeholder="货品名称" />
              <el-input-number v-model="item.qty" :min="0" :max="999999" controls-position="right" />
              <el-input v-model="item.unit" class="yc-order__unit" maxlength="16" placeholder="单位" />
              <el-input-number v-model="item.weight_kg" :min="0" :max="999999999" :precision="3" controls-position="right" />
              <el-input-number v-model="item.volume_cm3" :min="0" :max="999999999" :precision="2" controls-position="right" />
              <el-button link type="danger" @click="removeItem(index)">删除</el-button>
            </div>
            <div class="yc-order__line">
              <el-button @click="addItem">添加一行</el-button>
              <span class="yc-order__hint">同名货品在提交时自动合并数量</span>
            </div>
          </div>
        </el-form-item>

        <el-button type="primary" native-type="submit" :loading="saving" :disabled="!!formErrorText">
          {{ editing ? '保存' : '创建' }}
        </el-button>
        <span v-if="formErrorText" class="yc-order__hint yc-order__hint--inline">{{ formErrorText }}</span>
      </el-form>

      <template #footer>
        <el-button @click="formOpen = false">取消</el-button>
      </template>
    </el-dialog>

    <!-- 详情 -->
    <el-dialog
      v-if="detailOpen"
      v-model="detailOpen"
      :title="detail ? `订单详情 · ${detail.order.doc_no}` : '订单详情'"
      width="860px"
    >
      <div v-if="detailError" class="yc-order__error">
        <TraceIdAlert :error="detailError" />
      </div>

      <div v-loading="detailLoading">
        <template v-if="detail">
          <div class="yc-order__facts">
            <div class="yc-order__fact"><span>状态</span><el-tag :type="STATUS_TAG[detail.order.status]" size="small">{{ STATUS_LABELS[detail.order.status] }}</el-tag></div>
            <div class="yc-order__fact"><span>来源</span>{{ SOURCE_LABELS[detail.order.source] }}</div>
            <div class="yc-order__fact"><span>客户</span>{{ detail.customer_name || `#${detail.order.customer_id}` }}</div>
            <div class="yc-order__fact"><span>收货人</span>{{ detail.order.consignee }} {{ detail.order.consignee_phone }}</div>
            <div class="yc-order__fact"><span>线路</span>{{ detail.order.origin }} → {{ detail.order.dest }}</div>
            <div class="yc-order__fact"><span>货品</span>{{ detail.order.cargo_desc || '—' }}</div>
            <div class="yc-order__fact"><span>里程 / 重量 / 体积</span>{{ detail.order.distance_km }} km / {{ detail.order.weight_kg }} kg / {{ detail.order.volume_cm3 }} cm³</div>
            <div class="yc-order__fact"><span>备注</span>{{ detail.order.remark || '—' }}</div>
          </div>

          <h4 class="yc-order__sect">货品明细</h4>
          <el-table :data="detail.items" size="small">
            <el-table-column prop="name" label="名称" min-width="140" />
            <el-table-column prop="qty" label="数量" width="90" align="right" />
            <el-table-column prop="unit" label="单位" width="80" />
            <el-table-column prop="weight_kg" label="重量（kg）" width="110" align="right" />
            <el-table-column prop="volume_cm3" label="体积（cm³）" width="120" align="right" />
          </el-table>

          <h4 class="yc-order__sect">计费明细</h4>
          <el-table v-if="detailCharges.length" :data="detailCharges" size="small">
            <el-table-column prop="charge_no" label="计费单号" width="170" />
            <el-table-column prop="rate_card_name" label="规则" min-width="130" show-overflow-tooltip />
            <el-table-column prop="qty" label="计费量" width="100" align="right" />
            <el-table-column label="金额" width="110" align="right">
              <template #default="{ row }">{{ Number(row.amount).toFixed(2) }}</template>
            </el-table-column>
            <el-table-column label="状态" width="96">
              <template #default="{ row }">{{ CHARGE_STATUS[row.status as ChargeStatus] ?? row.status }}</template>
            </el-table-column>
            <el-table-column label="创建时间" min-width="155">
              <template #default="{ row }">{{ formatDateTime(row.created_at) }}</template>
            </el-table-column>
          </el-table>
          <p v-else class="yc-order__hint">还没有计费明细，订单签收后点「计费」生成。</p>

          <h4 class="yc-order__sect">时间</h4>
          <div class="yc-order__facts">
            <div class="yc-order__fact"><span>创建时间</span>{{ formatDateTime(detail.order.created_at) }}</div>
            <div class="yc-order__fact"><span>更新时间</span>{{ formatDateTime(detail.order.updated_at) }}</div>
            <div class="yc-order__fact"><span>期望提货</span>{{ detail.order.expect_pickup_at ? formatDateTime(detail.order.expect_pickup_at) : '—' }}</div>
            <div class="yc-order__fact"><span>期望送达</span>{{ detail.order.expect_deliver_at ? formatDateTime(detail.order.expect_deliver_at) : '—' }}</div>
            <div class="yc-order__fact"><span>创建人</span>{{ detail.order.created_by || '—' }}</div>
          </div>
        </template>
      </div>

      <template #footer>
        <el-button @click="detailOpen = false">关闭</el-button>
      </template>
    </el-dialog>

    <!-- 计费 -->
    <el-dialog
      v-if="chargeOpen"
      v-model="chargeOpen"
      :title="chargeRow ? `订单计费 · ${chargeRow.doc_no}` : '订单计费'"
      width="480px"
      :close-on-click-modal="false"
    >
      <div v-if="chargeError" class="yc-order__error">
        <TraceIdAlert :error="chargeError" />
      </div>

      <el-form label-position="top" @submit.prevent="doCharge">
        <el-form-item label="计费规则">
          <el-select v-model="chargeRateId" class="yc-order__ref" clearable placeholder="自动（仅一条启用规则时可用）">
            <el-option
              v-for="card in rateCards"
              :key="card.id"
              :label="`${card.code} ${card.name}`"
              :value="card.id"
            />
          </el-select>
          <span class="yc-order__hint">
            有多条启用规则时必须指定，否则后端回 422；计费量取订单的里程 / 重量 / 重量×里程 / 单量。
          </span>
        </el-form-item>

        <el-button type="primary" native-type="submit" :loading="charging">开始计费</el-button>
      </el-form>

      <template #footer>
        <el-button @click="chargeOpen = false">取消</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped lang="scss">
.yc-order {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-4);
  }

  &__keyword {
    width: 180px;
  }

  &__ref {
    width: 190px;
  }

  &__status {
    width: 130px;
  }

  &__small {
    width: 170px;
  }

  &__notice {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__page {
    margin-top: var(--yc-space-4);
    justify-content: flex-end;
  }

  &__formrow {
    display: flex;
    flex-wrap: wrap;
    gap: var(--yc-space-3);

    .el-form-item {
      margin-bottom: var(--yc-space-3);
    }
  }

  &__lines {
    width: 100%;
  }

  &__line {
    display: flex;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-2);
  }

  &__name {
    width: 180px;
  }

  &__unit {
    width: 90px;
  }

  &__hint {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-xs);
    line-height: 1.6;

    &--inline {
      margin-left: var(--yc-space-3);
    }
  }

  &__sect {
    margin: var(--yc-space-5) 0 var(--yc-space-3);
    font-size: var(--yc-font);
    font-weight: 600;
  }

  &__facts {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--yc-space-2) var(--yc-space-4);
  }

  &__fact {
    display: flex;
    gap: var(--yc-space-2);
    font-size: var(--yc-font-sm);
    word-break: break-all;

    span {
      flex: none;
      color: var(--yc-text-secondary);
    }
  }
}
</style>
